import { useEffect, useState } from 'react';
import { useMemberDuplicationCheck } from '@/common/components/member-duplication/useMemberDuplicationCheck';
import { ClosedMemberDuplicationCheckDTO } from '@/api/DTO/member-management/memberDuplication';

/**
 * CHA-002 check point 3 (Admission Request → Member). Runs the member
 * duplication check when `trigger` bumps, then bumps `familyTrigger` so the
 * existing FamilyMemberLinkCheck → register form flow continues.
 * The picked closed member (`pendingClosedMember`) is shown on the register
 * form, and `linkFields()` sends it with the register request.
 */
export function useRegisterDuplicationCheck(trigger: number, details: any) {
	const { runDuplicationCheck, duplicationDialogs } =
		useMemberDuplicationCheck();
	const [familyTrigger, setFamilyTrigger] = useState(0);
	const [pendingClosedMember, setPendingClosedMember] =
		useState<ClosedMemberDuplicationCheckDTO | null>(null);

	useEffect(() => {
		if (!trigger || !details?.id) return;
		(async () => {
			setPendingClosedMember(null);
			const check = await runDuplicationCheck(
				{ userId: details.id },
				{
					skipClosedCheck: !!(
						details.linkedClosedUserId || details.linkedLegacyFileNo
					),
					confirmLink: false,
				}
			);
			if (!check.proceed) return;
			setPendingClosedMember(check.closedMember ?? null);
			setFamilyTrigger((prev) => prev + 1);
		})();
	}, [trigger]);

	const linkFields = () =>
		pendingClosedMember ? { closedUserId: pendingClosedMember.id } : {};

	return { familyTrigger, duplicationDialogs, pendingClosedMember, linkFields };
}
